// Copyright (c) Docugami, Inc. All rights reserved.

import { IButtonStyles, IconButton, IIconProps, Modal, PrimaryButton, TooltipHost } from "@fluentui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { dgFontFamily } from "components/common/Fonts";
import { LineChart } from "components/common/LineChart";
import { greyText, primaryTheme } from "components/shell/presentation/AppStyles";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { useGetLiveQaModelStatsQuery } from "store/frontend/frontendSlice";
import styled from 'styled-components';


const ModalTitle = styled.div`
    font-family: ${dgFontFamily};
    font-weight: 600;
    font-size: 18px;
    padding-left: 10px;
`

const ExplanationText = styled.span`
    font-family: ${dgFontFamily};
    font-weight: 400;
    font-size: 16px;
`

const ScoreContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 5px;
`

const Score = styled.div`
    font-family: ${dgFontFamily};
    font-weight: 500;
    font-size: 16px;
`

const ModalHeader = styled.div`
    display: flex;
    flex-direction: row;
    flex: '1 1 auto';
    align-items: center;
`

const ModalContent = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`

const ModalBody = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0px 10px 10px 10px;
`

const ModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    padding: 0px 12px 12px 12px;
`

const Spacer = styled.div`
    flex-grow: 1;
`

const iconButtonStyles: Partial<IButtonStyles> = {
    root: {
        color: primaryTheme.palette.neutralPrimary,
        marginLeft: 'auto',
        marginRight: '2px',
        alignItems: 'center',
    },
    rootHovered: {
        color: primaryTheme.palette.neutralDark,
    },
};

const cancelIcon: IIconProps = { iconName: 'Cancel' };


interface DocsetModelStatsDialogProps {
    docsetId: string,
    dismissCallback: () => void
}

export const DocsetModelStatsDialog = (props: DocsetModelStatsDialogProps): JSX.Element => {
    const dgIcons = useDgIcons();

    const { data: stats, isLoading: queryLoading } = useGetLiveQaModelStatsQuery(props.docsetId ?? skipToken);
    const latestModelStats = !!stats?.modelStats ? stats.modelStats[stats.modelStats.length - 1] : null

    function dialogBody(): JSX.Element {
        const f1Score = !!latestModelStats ? (latestModelStats.exactMatchF1Score).toFixed(0) : 'n/a';
        let confidenceTooltipContent = null;
        if (latestModelStats) {
            const confidence = latestModelStats.confidence < 0.5 ? 'low' : 'medium';
            confidenceTooltipContent = `The confidence level of this score is ${confidence}. Confidence will improve as more documents are confirmed.`;
        }
        return (
            <ModalBody>
                <ScoreContainer>
                    <Score>Current Accuracy: {f1Score}%</Score>
                    {!!confidenceTooltipContent && <TooltipHost content={confidenceTooltipContent}>
                        {dgIcons.icon(
                            dgIconNames.DgInfo,
                            '',
                            16,
                            16,
                            greyText,
                            ''
                        )}
                    </TooltipHost>
                    }
                </ScoreContainer>
                {!!stats ?
                    <LineChart
                        title="Historical Trend"
                        data={!!stats ? stats.modelStats.map((item) => item.exactMatchF1Score) : []}
                        label="Accuracy" /> :
                    <ExplanationText>Please confirm more documents to see model statistics.</ExplanationText>
                }
            </ModalBody>
        )
    }

    return (
        queryLoading ? <></> :
            <Modal
                isOpen={true}
                onDismiss={props.dismissCallback}
                isBlocking={false}
                isDarkOverlay={true}
                styles={{
                    root: {
                        overflow: 'hidden',
                    },
                    main: {
                        overflow: 'hidden',
                        borderRadius: '5px',
                        background: primaryTheme.palette.neutralLighterAlt
                    },
                    scrollableContent: {
                        maxHeight: '75vh',
                        maxWidth: '50vw',
                        minWidth: '400px',
                        minHeight: '50px',
                    }
                }}>
                <ModalContent>
                    <ModalHeader>
                        <ModalTitle>Accuracy History</ModalTitle>
                        <IconButton
                            styles={iconButtonStyles}
                            iconProps={cancelIcon}
                            ariaLabel="Close popup modal"
                            onClick={props.dismissCallback} />
                    </ModalHeader>
                    {dialogBody()}
                    <ModalFooter>
                        <Spacer />
                        <PrimaryButton
                            onClick={props.dismissCallback}
                            text="Close"
                            disabled={false} />
                    </ModalFooter>
                </ModalContent>
            </Modal>
    );
}
